import {priority} from '@priority-software/priority-proto-api/lib';
import {
    conditionOperatorMap,
    contextValueMap,
    logicalOperatorMap,
    ruleActionMap
} from './constants';
import {
    AIAlertResponse,
    IntervalRelativeToFieldConfig,
    RemainingInThisStatusConfig
} from '../../../../../services/rest/llm/types/automationRuleTypes';
import {RuleOperators} from '../../RuleDialog/enum';
import {RecipientsType} from '../../RuleDialog/parts/AutomationActionTemplate/BusinessRuleActionTemplate/enum';
import {AutomationChoose} from '../../../reducer/model';
import {RuleType} from '../../../enums';
import {formatDate, formatTime} from '../../../../Form/utils/date';
import {currentUserPlaceholder} from '../../../../../services/rest/llm/constants';
import {getUsername} from '../../../../../_shared/GWTBridge/invoke/genPage';
import {isValidEmail} from '@priority-software/priority-utils/lib/email';
import {AIDateOperator} from '../../../../../services/rest/enums';
import {getColumnTypeKey} from '../../../../../utils/columns';
import {isHour12Format} from '../../../../Form/utils/time';
import {getLang} from '../../../../../Localization';
import {getBasicLanguage} from '../../../../../_shared/GWTBridge/invoke/common';
import {ENGLISH_INSTALLATION_LANGUAGE} from '../../RuleDialog/parts/AutomationActionToolbar/constants';

const TimeUnits = {
    MINUTES: 0,
    HOURS: 1,
    DAYS: 2,
    WEEKS: 3,
    MONTHS: 4
};

type ChoosesData = {
    fields: Array<{name: string; id: number; type: string}>;
    timeUnits: Array<AutomationChoose>;
    dateFields: Array<AutomationChoose>;
    availableStatuses: Array<AutomationChoose>;
    webhooks: Array<AutomationChoose>;
    users: Array<AutomationChoose>;
    groups: Array<AutomationChoose>;
    predefinedDateList: Array<{label: string; value: string}>;
};

const mapBpmConditionFields = (response: AIAlertResponse, chooses: ChoosesData) => {
    const {timeUnits, dateFields, availableStatuses} = chooses;

    const {timingCondition, timingConditionConfig} = response.condition || {};
    let BPM = {};

    if (timingCondition) {
        if (timingCondition === 'RECORD_HAS_TRANSITIONED_TO_THIS_STATUS') {
            BPM = {
                ...BPM,
                TimeField: undefined,
                TimeUnit: undefined,
                TimeValue: undefined
            };
        } else if (timingCondition === 'RECORD_HAS_REMAINED_IN_THIS_STATUS') {
            const config = (timingConditionConfig as RemainingInThisStatusConfig)?.remainedInStatus;
            BPM = {
                ...BPM,
                TimeUnit: timeUnits[TimeUnits[config?.timeUnit]]?.value as number,
                TimeValue: config.timeValue
            };
        } else if (response.condition?.timingCondition === 'INTERVAL_RELATIVE_TO_FIELD') {
            const config = (timingConditionConfig as IntervalRelativeToFieldConfig)
                .fieldDateInterval;

            BPM = {
                ...BPM,
                TimeField:
                    (chooses.fields.find(item => item.name === config?.field)?.id as number) ||
                    (dateFields[0]?.value as number), // fallback to first date field,
                TimeUnit: timeUnits[TimeUnits[config?.intervalUnit]]?.value as number,
                TimeValue:
                    config?.intervalDirection === 'TOOK_PLACE_BEFORE'
                        ? 1
                        : -1 * (config?.intervalValue || 0)
            };
        }
    }
    if (response.actionConfig?.changeStatusTo) {
        BPM = {
            ...BPM,
            ToStatus: availableStatuses.find(
                item => item.label === response.actionConfig?.changeStatusTo?.status
            )?.value as number
        };
    }

    return BPM;
};

const formatValue = (
    value: string = '',
    columnType: priority.netitems.form.ColumnType,
    predefinedDateList: Array<{value: string; label: string}>
) => {
    if (columnType === priority.netitems.form.ColumnType.Time5) {
        return formatTime(value, isHour12Format(getLang()));
    } else if (
        columnType === priority.netitems.form.ColumnType.Date8 ||
        columnType === priority.netitems.form.ColumnType.Date10 ||
        columnType === priority.netitems.form.ColumnType.Date14
    ) {
        const date = new Date(value);
        if (date.toString() !== 'Invalid Date') {
            return formatDate(date, columnType);
        }
        return predefinedDateList[AIDateOperator[value]]?.value || '';
    }
    return value;
};

export const findByLabel = (arr: Array<{label: string; value: unknown}> = [], label: string) => {
    return arr.find(option => option.label === label);
};

export const mapAIResponseToRule = (
    response: AIAlertResponse,
    type: RuleType,
    chooses: ChoosesData,
    newEditorSupport?: boolean
) => {
    const {fields, webhooks, users, groups, predefinedDateList} = chooses;
    const currentUser = getUsername();
    const actionType = ruleActionMap[response.action];

    const alertData: priority.netitems.form.IAlert = {
        Description: response.description,
        Active: true,
        Form: {
            ValidMySelf: true,
            MultiComp: 'ALL_COMPANIES' === response.scope?.company
        },
        BPM: {},
        Messages: [],
        CoverLetters: [],
        MessageType: actionType,
        ConditionsOperator: logicalOperatorMap[response.condition?.logicOperator],
        AlertConds: (response.condition?.rules || []).map(condition => {
            const condOperator = conditionOperatorMap[condition.operator];
            const isBetween = condOperator === RuleOperators.fallsBetween;
            const field = fields.find(f => f.name === condition.field);
            const fieldType = field?.type
                ? priority.netitems.form.ColumnType[field.type]
                : undefined;

            return new priority.netitems.form.AlertCondition({
                CurrentValue: contextValueMap[condition.context],
                ConditionField: field?.id,
                ConditionOperator: condOperator,
                ConditionValue: formatValue(
                    isBetween ? condition.values[0] : condition.values[0],
                    fieldType,
                    predefinedDateList
                ),
                ConditionSecondValue: formatValue(
                    condition.values[1],
                    fieldType,
                    predefinedDateList
                )
            });
        })
    };

    const messageData = response.actionConfig?.displayMessage || response.actionConfig?.sendMessage;
    const webhookData = response.actionConfig?.sendWebhook;
    const emailData = response?.actionConfig?.sendMessage;

    const recipientType =
        emailData?.recipientType || response?.actionConfig?.sendMessage?.recipientType;
    const recipientData = emailData?.recipients || response?.actionConfig?.sendMessage?.recipients;

    const emailLikeData = emailData || webhookData || messageData;

    const basicLanguage = getBasicLanguage(); // system installation language

    if (actionType === ruleActionMap.SEND_EMAIL && newEditorSupport) {
        alertData.CoverLetters = [
            {
                Message: prepareTextForEmail(emailLikeData?.bodyEnglish),
                Subject: emailLikeData.subjectEnglish,
                Language: 3
            },
            ...(basicLanguage !== ENGLISH_INSTALLATION_LANGUAGE
                ? [
                      {
                          Message: prepareTextForEmail(emailLikeData.bodyHebrew),
                          Subject: emailLikeData.subjectHebrew,
                          Language: 1
                      }
                  ]
                : [])
        ];
    } else if (emailLikeData) {
        alertData.Messages = [
            {
                Message: emailLikeData?.bodyEnglish,
                Subject: emailLikeData.subjectEnglish,
                Language: 3
            },
            ...(basicLanguage !== ENGLISH_INSTALLATION_LANGUAGE
                ? [
                      {
                          Message: emailLikeData.bodyHebrew,
                          Subject: emailLikeData.subjectHebrew,
                          Language: 1
                      }
                  ]
                : [])
        ];
    }

    if (webhookData?.endpoint) {
        alertData.WebHook = webhooks.find(item => item.label === webhookData.endpoint)
            ?.value as number;
    }

    if (recipientType === 'USER') {
        alertData.MailType = RecipientsType.user;

        alertData.RecipientUser = recipientData
            .map(user => {
                return findByLabel(users, user === currentUserPlaceholder ? currentUser : user)
                    ?.value as number;
            })
            .filter(Boolean);
    } else if (recipientType === 'GROUP') {
        alertData.MailType = RecipientsType.group;
        alertData.RecipientGroup = recipientData
            .map(group => findByLabel(groups, group)?.value as number)
            .filter(Boolean);
    } else if (recipientType === 'FIELD_FROM_FORM') {
        alertData.MailType = RecipientsType.field;
        const recipient = recipientData[0]; // Only one recipient is supported for such case

        alertData.RecipientField = chooses.fields.find(({name}) => name === recipient)?.id;
    } else if (recipientType === 'PHONE_NUMBER') {
        alertData.MailType = RecipientsType.phone;
        alertData.RecipientMail = response?.actionConfig?.sendMessage?.recipients;
    } else if (recipientType === 'ADDRESS') {
        alertData.MailType = RecipientsType.email;
        alertData.RecipientMail = emailData?.recipients?.filter(address => isValidEmail(address));
    }

    const assignToOrConditionalTransition = response?.actionConfig?.assignToOrConditionalTransition;

    if (assignToOrConditionalTransition) {
        const entityValues = assignToOrConditionalTransition.entityValues || [];
        if (assignToOrConditionalTransition.entityType === 'USER') {
            alertData.MailType = RecipientsType.user;

            alertData.RecipientUser = entityValues
                .map(user => {
                    return findByLabel(users, user === currentUserPlaceholder ? currentUser : user)
                        ?.value as number;
                })
                .filter(Boolean);
        } else if (assignToOrConditionalTransition?.entityType === 'FIELD_FROM_FORM') {
            alertData.MailType = RecipientsType.field;
            const recipient = entityValues[0]; // Only one recipient is supported for such case

            alertData.RecipientField = fields.find(({name}) => name === recipient)?.id;
        } else if (assignToOrConditionalTransition?.entityType === 'GROUP') {
            alertData.MailType = RecipientsType.group;

            alertData.RecipientGroup = entityValues
                .map(group => findByLabel(groups, group)?.value as number)
                .filter(Boolean);
        }
    }

    if (type === RuleType.autofill) {
        // handle autofill rule params
        if (response.updateField) {
            // Autofill source field
            alertData.RecipientField = fields.find(f => f.name === response.updateField)?.id;
            const updateField = fields.find(f => f.name === response.updateField);
            const updateFieldType = updateField?.type
                ? priority.netitems.form.ColumnType[updateField.type]
                : undefined;

            alertData.RecipientMail = [
                formatValue(response.updateValue, updateFieldType, predefinedDateList)
            ];
        }

        if (response.field) {
            // Autofill update field
            alertData.Form.UpdateField = fields.find(f => f.name === response.field)?.id;
        }
    }

    if (type === RuleType.bpm) {
        alertData.BPM = mapBpmConditionFields(response, chooses);
    }

    return new priority.netitems.form.Alert(alertData);
};

export const prepareFieldsData = (
    data: Array<AutomationChoose>,
    valueFields: Array<string>,
    type: RuleType
) => {
    return data.map(c => {
        const defaultFields = {
            title: c.label,
            id: Number(c.value)
        };
        if (type === RuleType.bpm) {
            const extraInfo = c.data as {Name: string; Type: string};

            return {
                ...defaultFields,
                autofill: false,
                name: extraInfo?.Name || '',
                type: extraInfo.Type || 'Str'
            };
        }
        const extraInfo = c.data as priority.netitems.form.IAlertChoose;
        const columnName = extraInfo?.ColName || '';

        return {
            ...defaultFields,
            name: columnName,
            type: getColumnTypeKey(extraInfo?.ColType) || 'Str',
            autofill:
                type === RuleType.autofill && columnName
                    ? valueFields.indexOf(columnName) > -1
                    : false
        };
    });
};

const prepareTextForEmail = (text?: string) => {
    if (text) return `<p>${text}</p>`;
    return '';
};

const AI_PROMOTION_KEY = 'ai_automation_rules_promotion';
export const checkAIFeaturePromotion = () => {
    return window.localStorage.getItem(AI_PROMOTION_KEY) !== '0';
};

export const hideAIFeaturePromotion = () => {
    window.localStorage.setItem(AI_PROMOTION_KEY, '0');
};

export const prepareValueFieldsList = (data: AutomationChoose[]) => {
    return data
        .map(field => {
            const data = field.data as priority.netitems.form.IAlertChoose;
            if (data?.ReadOnly) return '';

            return (data?.ColName as string) || '';
        })
        .filter(Boolean);
};
